import { useEffect, useState } from 'react';
import type { IndexWeightConfig, SearchWeight } from '../types';
import { SearchModeToggle } from './SearchModeToggle';

interface WeightManagerProps {
  config: IndexWeightConfig;
  onSave: (config: IndexWeightConfig) => void;
  onReset: () => void;
  saving: boolean;
}

export function WeightManager({ config, onSave, onReset, saving }: WeightManagerProps) {
  const [localConfig, setLocalConfig] = useState<IndexWeightConfig>(config);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentMode, setCurrentMode] = useState<'regular' | 'research'>('regular');

  useEffect(() => {
    setLocalConfig(config);
    setHasChanges(false);
  }, [config]);

  // Initialize research weights if they don't exist
  useEffect(() => {
    if (currentMode === 'research' && !localConfig.weightsResearch) {
      // Clone regular weights as initial research weights
      setLocalConfig({
        ...localConfig,
        weightsResearch: JSON.parse(JSON.stringify(localConfig.weights)),
      });
    }
  }, [currentMode, localConfig]);

  // Get the current weights based on the selected mode
  const getCurrentWeights = (): SearchWeight[] => {
    if (currentMode === 'research' && localConfig.weightsResearch) {
      return localConfig.weightsResearch;
    }
    return localConfig.weights;
  };

  const handleWeightChange = (index: number, value: string) => {
    const newWeight = parseFloat(value);
    if (!Number.isNaN(newWeight) && newWeight >= 0 && newWeight <= 10) {
      const currentWeights = getCurrentWeights();
      const updatedWeights = [...currentWeights];
      updatedWeights[index] = { ...updatedWeights[index], weight: newWeight };

      if (currentMode === 'research') {
        setLocalConfig({ ...localConfig, weightsResearch: updatedWeights });
      } else {
        setLocalConfig({ ...localConfig, weights: updatedWeights });
      }
      setHasChanges(true);
    }
  };

  const handleEnabledChange = (index: number) => {
    const currentWeights = getCurrentWeights();
    const updatedWeights = [...currentWeights];
    updatedWeights[index] = {
      ...updatedWeights[index],
      enabled: !updatedWeights[index].enabled,
    };

    if (currentMode === 'research') {
      setLocalConfig({ ...localConfig, weightsResearch: updatedWeights });
    } else {
      setLocalConfig({ ...localConfig, weights: updatedWeights });
    }
    setHasChanges(true);
  };

  const [showAddFieldDropdown, setShowAddFieldDropdown] = useState(false);

  // Available fields per index type - based on OpenSearch mapping and common fields
  const getAvailableFields = (indexName: string): string[] => {
    const fieldMap: Record<string, string[]> = {
      'asmi-dl-books': [
        'title',
        'book_title',
        'abstract',
        'content',
        'keywords',
        'subjects',
        'authors',
        'editors',
        'collection_title',
        'publisher',
        'isbn',
        'publication_format',
        'doi',
        'volume_string',
        'series',
      ],
      'asmi-dl-chapters': [
        'title',
        'abstract',
        'content',
        'keywords',
        'subjects',
        'book_title',
        'publisher',
        'chapter_number',
        'doi',
        'collection_title',
        'authors',
      ],
      'asmi-dl-chapter-images': [
        'title',
        'image_caption',
        'content',
        'keywords',
        'subjects',
        'book_title',
        'chapter_title',
        'figure_number',
        'image_type',
        'collection_title',
        'authors',
      ],
      'asmi-dl-chapter-tables': [
        'title',
        'table_caption',
        'content',
        'keywords',
        'subjects',
        'book_title',
        'chapter_title',
        'table_number',
        'collection_title',
        'authors',
      ],
      'asmi-wp-products': [
        'title',
        'content',
        'description',
        'sku',
        'product_type',
        'producttype',
        'format',
        'classformat',
        'venue',
        'locationcity',
        'locationstate',
        'locationcountry',
        'instructor',
        'instructorbio',
        'classoutlinedescription',
        'categories',
      ],
      'asmi-dl-proceedings': [
        'title',
        'abstract',
        'content',
        'keywords',
        'subjects',
        'authors',
        'publisher',
        'conference_name',
        'conference_acronym',
        'conference_location',
        'conference_date',
        'volume',
        'isbn',
        'doi',
      ],
      'asmi-dl-technical_articles': [
        'title',
        'abstract',
        'content',
        'keywords',
        'subjects',
        'authors',
        'publisher',
        'volume',
        'issue',
        'page_range',
        'issn',
        'doi',
        'magazines',
        'journal_title',
      ],
      'asmi-dl-datasheets': [
        'title',
        'abstract',
        'content',
        'keywords',
        'subjects',
        'publisher',
        'publisher_id',
        'product',
        'volume',
        'issue',
        'issn',
        'doi',
      ],
      'asmi-wp-events': [
        'title',
        'content',
        'description',
        'keywords',
        'subjects',
        'course_outline',
        'event_type',
        'event_city',
        'event_state',
        'event_country',
        'event_street',
        'event_start_date',
        'event_end_date',
        'member_discount',
        'course_format',
        'event_category',
        'event_location',
        'sku',
        'instructor',
      ],
      'asmi-wp-asm-news': ['title', 'content', 'description', 'categories', 'published_date', 'source', 'keywords'],
      'asmi-pages-events': [
        'title',
        'content',
        'description',
        'keywords',
        'subjects',
        'event_type',
        'event_city',
        'event_state',
        'event_country',
        'event_date',
        'event_location',
        'url',
        'permalink',
      ],
      'asmi-wp-news_articles': [
        'title',
        'content',
        'description',
        'categories',
        'published_date',
        'source',
        'keywords',
        'journal_title',
        'series_title',
      ],
      'asmi-wp-pages': ['title', 'content', 'description', 'categories', 'url', 'permalink'],
      'asmi-wp-videos': [
        'title',
        'content',
        'description',
        'keywords',
        'video_url',
        'duration',
        'categories',
        'published_date',
        'speaker',
        'topics',
      ],
      // Materials Data indexes
      'asmi-mat-matdb-materials': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-alloyfinder-alloy-finder': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-datasheets-data-sheets-and-diagrams': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-grantami_mgd-micrograph-data': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-corrosion-uniform-corrosion-data': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-corrosion-environments': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-matdb-documents': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-datasheets-data-sheet-sources': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-matdb-reference': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-corrosion-design-data': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-corrosion-references': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-alloyfinder-organizations': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-corrosion-materials': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
      'asmi-mat-corrosion-high-temperature-oxidation-data': [
        'Material',
        'attributes.value',
        'attributes.name',
        'id',
        '_root_',
        'abstract',
        'description',
      ],
    };

    return fieldMap[indexName] || ['title', 'content', 'description', 'keywords', 'subjects'];
  };

  const handleAddField = (fieldName: string) => {
    const currentWeights = getCurrentWeights();
    if (fieldName && !currentWeights.some((w) => w.field === fieldName)) {
      const newWeight: SearchWeight = {
        field: fieldName,
        weight: 1,
        enabled: true,
      };

      if (currentMode === 'research') {
        setLocalConfig({
          ...localConfig,
          weightsResearch: [...currentWeights, newWeight],
        });
      } else {
        setLocalConfig({
          ...localConfig,
          weights: [...currentWeights, newWeight],
        });
      }
      setHasChanges(true);
    }
    setShowAddFieldDropdown(false);
  };

  const getUnusedFields = (): string[] => {
    const availableFields = getAvailableFields(localConfig.indexName);
    const currentWeights = getCurrentWeights();
    const usedFields = currentWeights.map((w) => w.field);
    return availableFields.filter((field) => !usedFields.includes(field));
  };

  const handleRemoveField = (index: number) => {
    const currentWeights = getCurrentWeights();
    if (window.confirm(`Remove field "${currentWeights[index].field}"?`)) {
      const updatedWeights = currentWeights.filter((_, i) => i !== index);

      if (currentMode === 'research') {
        setLocalConfig({ ...localConfig, weightsResearch: updatedWeights });
      } else {
        setLocalConfig({ ...localConfig, weights: updatedWeights });
      }
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    onSave(localConfig);
    setHasChanges(false);
  };

  const getTotalWeight = () => {
    const currentWeights = getCurrentWeights();
    return currentWeights
      .filter((w) => w.enabled)
      .reduce((sum, w) => sum + w.weight, 0)
      .toFixed(1);
  };

  const getFieldDescription = (fieldName: string): string => {
    const descriptions: Record<string, string> = {
      title: 'Document titles - most important for relevance',
      content: 'Main document text content',
      description: 'Document descriptions and abstracts',
      keywords: 'Tagged keywords and terms',
      subjects: 'Subject classifications and topics',
      authors: 'Author names and contributors',
      abstract: 'Document abstracts and summaries',
      book_title: 'Book or publication titles',
      publisher: 'Publishing organization',
      categories: 'Content categories and classifications',
      isbn: 'International Standard Book Numbers',
      doi: 'Digital Object Identifiers',
      published_date: 'Publication dates',
      source: 'Content source or origin',
      course_outline: 'Detailed course outline and curriculum content',
      instructor: 'Course instructor name and information',
      sku: 'Product or course SKU/catalog number',
    };
    return descriptions[fieldName] || 'Document field for search matching';
  };

  const getWeightLabel = (weight: number): string => {
    if (weight === 0) return 'Disabled';
    if (weight <= 1) return 'Low Priority';
    if (weight <= 2) return 'Normal';
    if (weight <= 4) return 'High Priority';
    if (weight <= 6) return 'Very High';
    return 'Maximum Priority';
  };

  const getWeightColor = (weight: number): string => {
    if (weight < 0.5) return '#ff4444';
    if (weight < 1.0) return '#ff8800';
    if (weight === 1.0) return '#888888';
    if (weight <= 1.5) return '#44aa44';
    if (weight <= 2.0) return '#2266dd';
    return '#8844dd';
  };

  return (
    <div className="space-y-6">
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          background: var(--boost-color, #18181b);
        }
        input[type="range"]::-moz-range-thumb {
          background: var(--boost-color, #18181b);
        }
      `}</style>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">Search Field Weights Configuration</h3>
          <p className="mt-1 text-sm text-zinc-600">
            Control how different document fields influence search relevance. Higher weights make fields more important
            for ranking results.
          </p>
        </div>
        <SearchModeToggle currentMode={currentMode} onModeChange={setCurrentMode} />
      </div>

      {/* Explanation Section */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-base font-semibold text-blue-900 mb-4">How Field Weights Work</h3>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Purpose</h4>
            <p className="text-sm text-zinc-700 leading-relaxed">
              Field weights determine how much each document field contributes to the overall relevance score when users
              search your content.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">How It's Applied</h4>
            <p className="text-sm text-zinc-700 leading-relaxed mb-2">
              <strong>Field Score = Weight × Query Match Quality</strong>
            </p>
            <p className="text-sm text-zinc-700 leading-relaxed">
              Higher weights amplify matches in important fields like titles, while lower weights reduce the impact of
              less relevant fields.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Example</h4>
            <ul className="space-y-1">
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Title match: 3.0 × 0.95 = 2.85
              </li>
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Content match: 1.0 × 0.75 = 0.75
              </li>
              <li className="bg-white/70 rounded px-3 py-1.5 text-xs font-mono text-zinc-800">
                Keywords match: 2.0 × 0.60 = 1.20
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Content Type Info */}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
        <h4 className="font-medium text-zinc-900">{localConfig.displayName}</h4>
        <p className="mt-1 text-sm text-zinc-600">
          Index:{' '}
          <code className="rounded bg-zinc-200 px-1.5 py-0.5 text-xs font-mono text-zinc-700">
            {localConfig.indexName}
          </code>
        </p>
      </div>

      {/* Field Weight Configuration */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-zinc-900">Field Weight Configuration</h4>
          <div className="flex gap-4 text-sm text-zinc-600">
            <span>
              Active Fields:{' '}
              <strong className="text-zinc-900">{getCurrentWeights().filter((w) => w.enabled).length}</strong>
            </span>
            <span>
              Total Weight: <strong className="text-zinc-900">{getTotalWeight()}</strong>
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {getCurrentWeights().map((weight, index) => (
            <div
              key={`${weight.field}-${index}`}
              className={`rounded-lg border p-4 transition-colors ${
                weight.enabled ? 'border-zinc-200 bg-white' : 'border-zinc-100 bg-zinc-50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={weight.enabled}
                    onChange={() => handleEnabledChange(index)}
                    className="mt-1 h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                  />
                  <div className="flex-1">
                    <div className={`font-medium ${weight.enabled ? 'text-zinc-900' : 'text-zinc-500'}`}>
                      {weight.field}
                    </div>
                    <div className="text-sm text-zinc-600 mt-0.5">{getFieldDescription(weight.field)}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveField(index)}
                  className="text-zinc-400 hover:text-zinc-600 text-xl font-light"
                  title="Remove field"
                >
                  ×
                </button>
              </div>

              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={weight.weight}
                  onChange={(e) => handleWeightChange(index, e.target.value)}
                  disabled={!weight.enabled}
                  className="flex-1 h-2 rounded-lg appearance-none disabled:opacity-50 cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 disabled:[&::-webkit-slider-thumb]:cursor-not-allowed [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-md"
                  style={
                    {
                      background: weight.enabled
                        ? `linear-gradient(to right, ${getWeightColor(weight.weight)} 0%, ${getWeightColor(weight.weight)} ${(weight.weight / 10) * 100}%, #e4e4e7 ${(weight.weight / 10) * 100}%, #e4e4e7 100%)`
                        : '#e4e4e7',
                      ['--boost-color' as string]: weight.enabled ? getWeightColor(weight.weight) : '#a1a1aa',
                    } as React.CSSProperties
                  }
                />
                <div className="flex flex-col items-end min-w-[100px]">
                  <div className={`text-lg font-semibold ${weight.enabled ? 'text-zinc-900' : 'text-zinc-400'}`}>
                    {weight.weight}
                  </div>
                  <div
                    className="text-xs font-medium uppercase tracking-wide"
                    style={{ color: weight.enabled ? getWeightColor(weight.weight) : '#a1a1aa' }}
                  >
                    {getWeightLabel(weight.weight)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Field */}
        <div className="relative">
          {getUnusedFields().length > 0 ? (
            <>
              <button
                type="button"
                onClick={() => setShowAddFieldDropdown(!showAddFieldDropdown)}
                className="rounded-lg border-2 border-dashed border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50"
              >
                + Add Field
              </button>
              {showAddFieldDropdown && (
                <div className="absolute left-0 mt-2 w-64 rounded-lg border border-zinc-200 bg-white shadow-lg z-10">
                  <div className="border-b border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-900">
                    Select a field to add:
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {getUnusedFields().map((field) => (
                      <button
                        key={field}
                        onClick={() => handleAddField(field)}
                        className="block w-full px-4 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50"
                      >
                        {field}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-zinc-200 p-2">
                    <button
                      onClick={() => setShowAddFieldDropdown(false)}
                      className="w-full rounded px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-zinc-600">All available fields are already configured</div>
          )}
        </div>
      </div>

      {/* Field Priority Preview */}
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
        <h4 className="text-sm font-medium text-zinc-900 mb-3">Top Priority Fields</h4>
        <div className="space-y-2">
          {getCurrentWeights()
            .filter((w) => w.enabled)
            .sort((a, b) => b.weight - a.weight)
            .slice(0, 5)
            .map((weight, index) => (
              <div key={weight.field} className="flex items-center gap-3 text-sm">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200 text-xs font-semibold text-zinc-700">
                  {index + 1}
                </div>
                <div className="flex-1 font-medium text-zinc-900">{weight.field}</div>
                <div className="text-zinc-600">
                  {weight.weight}x ({getWeightLabel(weight.weight)})
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4 border-t border-zinc-200">
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          onClick={onReset}
          disabled={saving}
          className="rounded-lg border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset to Default
        </button>
      </div>

      {localConfig.updatedAt && (
        <div className="text-sm text-zinc-500">Last updated: {new Date(localConfig.updatedAt).toLocaleString()}</div>
      )}
    </div>
  );
}
